import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { CACHE_CONTROL_NO_CACHE, CACHE_CONTROL_SHORT_TTL } from '@src/constants';
import { LandingPageByAppNameWithPathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import { ChatBot, NextPage, NextPageHead, PageView, VWOExperimentPush } from '@src/lib/shared-components-web';
import { PageThemeContextProvider } from '@src/lib/shared-components-web/context/PageThemeProvider.ctx';
import { LAYOUT_FEATURE_FLAGS } from '@src/lib/vwo-fme/layout-feature-flags';
import { resolveFeatureFlags } from '@src/lib/vwo-fme/resolve-feature-flags';
import { resolveServerSideExperiments } from '@src/lib/vwo-fme/resolve-server-side-experiments';
import { fetchAppData } from '@utils/pages/app-data-fetcher';
import choosePreview from '@utils/pages/choose-preview';
import { describeContentfulError, throwIfContentfulUnavailable } from '@utils/pages/contentful-errors';
import DetermineIndexing from '@utils/pages/determine-indexing';
import getConfigByBrand from '@utils/pages/get-config-by-brand';
import getConfigServices from '@utils/pages/get-config-services';
import { getDeviceTypeFromHeaders } from '@utils/pages/get-device-type-from-headers';
import getHostFromHeaders from '@utils/pages/get-host-from-headers';
import { getServerSideTranslations } from '@utils/pages/get-serverside-translations';
import { handlePageLoadFailure } from '@utils/pages/handle-page-load-failure';
import { processPageContents } from '@utils/pages/page-contents-processor';
import { trimContentfulProps } from '@utils/pages/trim-contentful-props';
const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const pageData = useContentfulLiveUpdates(props.page);
  const {
    url: logoUrl
  } = props.appData?.logo ?? {};
  const {
    path,
    title,
    indexed,
    description,
    canonical,
    breadcrumbsCollection,
    disableChatBot = false,
    analyticsContext,
    ogImage
  } = pageData;
  const pageTheme = props.page?.pageTheme || 'Primary';
  const {
    siteURL,
    features,
    featureFlags,
    logo
  } = props.config as BrandThemeConfig;
  const {
    preview,
    pageReferrer,
    domain
  } = props;
  return <PageThemeContextProvider pageTheme={pageTheme} data-sentry-element="PageThemeContextProvider" data-sentry-component="Home" data-sentry-source-file="index.page.tsx">
			<PageView analyticsContext={analyticsContext} pageReferrer={pageReferrer} siteSection="Marketing" data-sentry-element="PageView" data-sentry-source-file="index.page.tsx" />
			<NextPageHead path={path} title={title} indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite, domain)} description={description} canonical={canonical} originUrl={siteURL} breadcrumbs={breadcrumbsCollection?.items} breadcrumbTitle={pageData.breadcrumbTitle} ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark} contentsCollection={pageData.contentsCollection} data-sentry-element="NextPageHead" data-sentry-source-file="index.page.tsx" />
			<VWOExperimentPush decisions={props.vwoDecisions ?? []} data-sentry-element="VWOExperimentPush" data-sentry-source-file="index.page.tsx" />
			<NextPage pageData={{
      ...pageData
    }} data-sentry-element="NextPage" data-sentry-source-file="index.page.tsx" />
			{!disableChatBot && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</PageThemeContextProvider>;
};
export const getServerSideProps: GetServerSideProps = async ({
  resolvedUrl,
  query,
  locale,
  req,
  res,
  preview = false
}) => {
  const domain = getHostFromHeaders(req?.headers);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const tagTheme: TagThemeType = 'lovet';
  const logger = getLogger('home', tagTheme);
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const brandConfig = getConfigByBrand(tagTheme);
    preview = choosePreview(domain, preview);
    const gqlClient = getClient(preview);
    if (gqlClient === null) {
      logger.warn('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
      throw new Error('gqlClient is null');
    }
    const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
    const appData = await fetchAppData(gqlClient, appName, preview, logger);
    if (!appData) {
      return {
        notFound: true
      };
    }
    const pageData = await gqlClient.query(LandingPageByAppNameWithPathDocument, {
      appName,
      path: '/',
      preview
    }).toPromise();
    throwIfContentfulUnavailable(pageData, 'CONTENTFUL_ERROR_HOME_PAGE');
    if (pageData.error) {
      logger.warn(`CONTENTFUL_ERROR_HOME_PAGE: ${describeContentfulError(pageData.error)}`);
    }
    const page = pageData.data?.pageCollection?.items[0] as PageFragment;
    if (!page) {
      logger.warn(`COULD_NOT_FIND_THE_HOME_PAGE`);
      return {
        notFound: true
      };
    }

    // Process page contents: fetch by ID, enrich with entry links
    const enrichedPageContents = await processPageContents(gqlClient, preview, logger, page.contentsCollection?.items, '/');
    // Resolve VWO FME feature flags for Server-Side ExperimentContainers
    const {
      decisions: experimentDecisions
    } = await resolveServerSideExperiments(enrichedPageContents, brandConfig as any, req, res, query, logger);

    // Resolve standalone VWO FME flags (layout-level header swap, etc.)
    const {
      flags: serverFeatureFlags,
      decisions: flagDecisions
    } = await resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, brandConfig as any, req, res, query, logger);
    const vwoDecisions = [...experimentDecisions, ...flagDecisions];
    if (preview) {
      res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
    } else if (!res.getHeader('Cache-Control')) {
      // Skip if an earlier resolver already pinned the response to a
      // non-shareable Cache-Control (e.g. visitor-id mint sets
      // "private, no-store" to prevent CDN cookie poisoning).
      res.setHeader('Cache-Control', CACHE_CONTROL_SHORT_TTL);
    }
    let hasIpBasedCoordinates = false;
    let coordinates = {
      latitude: brandConfig.defaultCoordinates.latitude,
      longitude: brandConfig.defaultCoordinates.longitude
    };
    if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
      hasIpBasedCoordinates = true;
      coordinates = {
        latitude: Number.parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
        longitude: Number.parseFloat(req.headers['cloudfront-viewer-longitude'] as string)
      };
      logger.debug(`CDN_GEO_LOCATION_FOUND: [${coordinates?.latitude}, ${coordinates?.longitude}]`);
    } else {
      logger.debug(`CDN_GEO_LOCATION_MISS`);
    }
    const transformedServices = getConfigServices(brandConfig as any);
    // Live Preview (useContentfulLiveUpdates) needs the untrimmed Contentful shape
    const trimForClient = <T,>(data: T): T => preview ? data : trimContentfulProps(data);
    return {
      props: {
        ...(await getServerSideTranslations(locale)),
        preview,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: transformedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        page: trimForClient({
          ...page,
          contentsCollection: {
            items: enrichedPageContents
          }
        }),
        appData: trimForClient(appData),
        user: {
          coordinates,
          hasIpBasedCoordinates
        },
        deviceType,
        pageReferrer,
        stateCodeFromHeaders,
        domain: domain ?? '',
        vwoDecisions,
        serverFeatureFlags
      }
    };
  } catch (e) {
    return handlePageLoadFailure(logger, resolvedUrl, e);
  }
};
export default Home;